import {
  Avatar,
  AvatarGroup,
  Box,
  CardActionArea,
  CardContent,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import { BoardProjectSurface, BoardMark } from './BoardSurfaces';
import type { BoardsQueryData } from '../../../generated/graphql';

type BoardSummary = BoardsQueryData['boards'][number];
type MemberSummary = BoardSummary['members'][number];

interface BoardCardThumbnailProps {
  board: BoardSummary;
}

function memberLabel(m: MemberSummary): string {
  const full = `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim();
  return full || m.email;
}

export function BoardCardThumbnail({ board }: BoardCardThumbnailProps) {
  const cardCount = board.columns.reduce((n, c) => n + c.cards.length, 0);

  return (
    <BoardProjectSurface
      variant="panel"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={`/boards/${board._id}`}
        sx={{ height: '100%' }}
      >
        <CardContent
          sx={{
            height: '100%',
            px: 2.5,
            pt: 2.5,
            pb: '16px !important',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <BoardMark>
              <ViewKanbanOutlinedIcon fontSize="small" />
            </BoardMark>
            <ArrowForwardIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              lineHeight: 1.3,
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              minHeight: '2.6rem',
            }}
          >
            {board.name}
          </Typography>
          <Box
            aria-hidden="true"
            sx={{
              display: 'flex',
              gap: 0.75,
              height: 92,
              mb: 2,
              overflow: 'hidden',
            }}
          >
            {board.columns.slice(0, 6).map((column) => (
              <Box
                key={column._id}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  p: 0.75,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    bgcolor: 'primary.main',
                    borderRadius: 1,
                    mb: 0.75,
                  }}
                />
                {column.cards.slice(0, 3).map((card) => (
                  <Box
                    key={card._id}
                    sx={{
                      height: 16,
                      bgcolor: 'action.selected',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 0.5,
                      mb: 0.5,
                    }}
                  />
                ))}
              </Box>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }}>
            {board.columns.length} stages · {board.members.length}{' '}
            {board.members.length === 1 ? 'collaborator' : 'collaborators'}
          </Typography>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pt: 1.5,
              mt: 'auto',
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box display="flex" alignItems="center" gap={0.5}>
              <Avatar
                role="img"
                aria-label={`${cardCount} ${cardCount === 1 ? 'card' : 'cards'}`}
                sx={{
                  width: 18,
                  height: 18,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                }}
              >
                {cardCount}
              </Avatar>
              <Typography variant="caption" color="text.secondary">
                {cardCount === 1 ? 'card' : 'cards'}
              </Typography>
            </Box>
            <AvatarGroup max={3}>
              {board.members.map((m) => (
                <Tooltip key={m._id} title={memberLabel(m)}>
                  <Avatar
                    sx={{
                      width: 22,
                      height: 22,
                      fontSize: '0.7rem',
                    }}
                  >
                    {`${m.firstName?.[0] ?? ''}${m.lastName?.[0] ?? ''}`.toUpperCase()}
                  </Avatar>
                </Tooltip>
              ))}
            </AvatarGroup>
          </Box>
        </CardContent>
      </CardActionArea>
    </BoardProjectSurface>
  );
}
